import { z } from 'zod';
import { balanceAmountStringSchema } from '../currency';
import { itemDefinitionIdSchema } from '../inventory/definitions';
import { raritySchema } from '../rarity';
import { DIRECT_TRADE_FEE_POINTS } from './fee';
import { MARKET_FEE_NON_VIP_PERCENT, MARKET_FEE_VIP_PERCENT } from './marketplace';
import { TRADE_STATES } from './states';

/**
 * The direct trade's wire contract.
 *
 * Every mutation carries the revision the client believes it is changing and an
 * idempotency key, and NONE of them carries an account id: the participant is
 * the authenticated session, and the counterparty is a property of the trade
 * row rather than of the request. Every request schema is `.strict()`, so an
 * unknown field is a rejected request rather than a silently ignored one.
 *
 * Amounts are decimal STRINGS. A JSON number is a double to every client in
 * between, and these are wallet quantities.
 */

export const TRADES_INBOX_ROUTE = '/api/trades/inbox' as const;
export const TRADES_PLAYER_SEARCH_ROUTE = '/api/trades/players' as const;
export const TRADE_INVITATIONS_ROUTE = '/api/trades/invitations' as const;
/** `:tradeId` routes, as templates. The client substitutes the id. */
export const TRADE_DETAIL_ROUTE = '/api/trades/:tradeId' as const;
export const TRADE_RESPOND_ROUTE = '/api/trades/:tradeId/respond' as const;
export const TRADE_OFFER_ROUTE = '/api/trades/:tradeId/offer' as const;
export const TRADE_FEE_ROUTE = '/api/trades/:tradeId/fee-contribution' as const;
export const TRADE_ACCEPT_OFFER_ROUTE = '/api/trades/:tradeId/accept-offer' as const;
export const TRADE_FINAL_CONFIRM_ROUTE = '/api/trades/:tradeId/final-confirm' as const;
export const TRADE_CANCEL_ROUTE = '/api/trades/:tradeId/cancel' as const;

export const tradeRoute = (tradeId: string, suffix = '') => `/api/trades/${tradeId}${suffix}`;

export const TRADE_CONTRACT_VERSION = 1;

/** Inbox page size. Small: a trade inbox is a working list, not an archive. */
export const TRADE_INBOX_PAGE_SIZE_DEFAULT = 20;
export const TRADE_INBOX_PAGE_SIZE_MAX = 50;

/** How many distinct item lines one side may offer. Bounded, not unbounded. */
export const TRADE_MAX_OFFER_LINES = 24;

/** Public display name only — an e-mail is never a search key and never a result. */
export const PLAYER_SEARCH_MIN_LENGTH = 3;
export const PLAYER_SEARCH_MAX_LENGTH = 40;
export const PLAYER_SEARCH_MAX_RESULTS = 10;

const idempotencyKeySchema = z.string().min(8).max(128);
/** Monotonic per trade. Every mutation states the one it expects to change. */
const revisionSchema = z.number().int().min(0).max(1_000_000);

export const tradeStateSchema = z.enum(TRADE_STATES);

/* -------------------------------------------------------------------------- */
/*  Requests                                                                  */
/* -------------------------------------------------------------------------- */

export const playerSearchQuerySchema = z
  .object({
    name: z.string().min(PLAYER_SEARCH_MIN_LENGTH).max(PLAYER_SEARCH_MAX_LENGTH),
  })
  .strict();

export const tradeInboxQuerySchema = z
  .object({
    cursor: z.string().min(1).max(200).optional(),
    limit: z.coerce.number().int().min(1).max(TRADE_INBOX_PAGE_SIZE_MAX).optional(),
  })
  .strict();

export const tradeInvitationRequestSchema = z
  .object({
    /** The invitee's ACCOUNT id, obtained from the player search. */
    counterpartyId: z.string().uuid(),
    idempotencyKey: idempotencyKeySchema,
  })
  .strict();

export const tradeRespondRequestSchema = z
  .object({
    accept: z.boolean(),
    idempotencyKey: idempotencyKeySchema,
  })
  .strict();

/**
 * One line of an offer.
 *
 * An instance line names the instance and nothing else — its definition, level
 * and rarity are the vault's answer, and a client that could state them could
 * state a lie. A stack line names the definition and an exact quantity, because
 * a stack has no per-unit identity to name.
 */
export const tradeOfferLineSchema = z.union([
  z.object({ kind: z.literal('instance'), instanceId: z.string().uuid() }).strict(),
  z
    .object({
      kind: z.literal('stack'),
      definitionId: itemDefinitionIdSchema,
      quantity: balanceAmountStringSchema,
    })
    .strict(),
]);

/**
 * The WHOLE of this participant's offer, every time.
 *
 * Declarative rather than incremental: the client sends what the offer should
 * be and the server reconciles reservations to match. An "add one item" verb
 * would make a lost response ambiguous — did it add? — and a retry would then
 * add twice. Restating the whole offer makes a replay a no-op by construction.
 */
export const tradeOfferRequestSchema = z
  .object({
    expectedRevision: revisionSchema,
    items: z.array(tradeOfferLineSchema).max(TRADE_MAX_OFFER_LINES),
    gold: balanceAmountStringSchema,
    cash: balanceAmountStringSchema,
    idempotencyKey: idempotencyKeySchema,
  })
  .strict();

/**
 * This participant's own fee contribution. Never the other's.
 *
 * There is deliberately no field naming the counterparty or their share: each
 * side controls only what they pay, and the server adds the two.
 */
export const tradeFeeContributionRequestSchema = z
  .object({
    expectedRevision: revisionSchema,
    gold: balanceAmountStringSchema,
    cash: balanceAmountStringSchema,
    idempotencyKey: idempotencyKeySchema,
  })
  .strict();

export const tradeAcceptOfferRequestSchema = z
  .object({
    /** The revision being accepted. An acceptance is bound to it. */
    expectedRevision: revisionSchema,
    /**
     * Required when this participant receives nothing at all.
     *
     * Not a payment confirmation and not evidence of anything outside the game:
     * it is an acknowledgement that this side of the trade is empty, so nobody
     * gives an item away believing something is coming back.
     */
    acknowledgeEmptySide: z.boolean().optional(),
    idempotencyKey: idempotencyKeySchema,
  })
  .strict();

export const tradeFinalConfirmRequestSchema = z
  .object({
    expectedRevision: revisionSchema,
    idempotencyKey: idempotencyKeySchema,
  })
  .strict();

export const tradeCancelRequestSchema = z
  .object({
    idempotencyKey: idempotencyKeySchema,
  })
  .strict();

/* -------------------------------------------------------------------------- */
/*  Responses                                                                 */
/* -------------------------------------------------------------------------- */

/** An item as a trade panel draws it. No owner, no internal state name. */
export const tradeItemWireSchema = z.object({
  kind: z.enum(['instance', 'stack']),
  /** Present for an instance line. */
  instanceId: z.string().uuid().nullable(),
  definitionId: itemDefinitionIdSchema,
  rarity: raritySchema,
  level: z.number().int().min(1),
  quantity: balanceAmountStringSchema,
});

export const tradeParticipantWireSchema = z.object({
  accountId: z.string().uuid(),
  /** Public display name. An e-mail never crosses this boundary. */
  displayName: z.string(),
  vipActive: z.boolean(),
  items: z.array(tradeItemWireSchema),
  offerGold: balanceAmountStringSchema,
  offerCash: balanceAmountStringSchema,
  feeGold: balanceAmountStringSchema,
  feeCash: balanceAmountStringSchema,
  /** Points this side has contributed. Derived; never sent by a client. */
  feePoints: balanceAmountStringSchema,
  /** The revision this side accepted, or null. */
  acceptedRevision: z.number().int().nullable(),
  finalConfirmedRevision: z.number().int().nullable(),
  emptySideAcknowledgedRevision: z.number().int().nullable(),
  /** True when this side receives NOTHING from the other. */
  receivesNothing: z.boolean(),
});

export const tradeFeeWireSchema = z.object({
  requiredPoints: balanceAmountStringSchema,
  totalPoints: balanceAmountStringSchema,
  remainingPoints: balanceAmountStringSchema,
  complete: z.boolean(),
  goldPointValue: balanceAmountStringSchema,
  cashPointValue: balanceAmountStringSchema,
});

/** One account's own wallet position, including what trades are holding. */
export const tradeWalletWireSchema = z.object({
  currency: z.enum(['gold', 'cash']),
  total: balanceAmountStringSchema,
  /** Held by offers, across every open trade. */
  reservedForOffers: balanceAmountStringSchema,
  /** Held by fee contributions, across every open trade. */
  reservedForFees: balanceAmountStringSchema,
  /** `total - reservedForOffers - reservedForFees`. Never negative. */
  available: balanceAmountStringSchema,
});

export const tradeWireSchema = z.object({
  contractVersion: z.literal(TRADE_CONTRACT_VERSION),
  tradeId: z.string().uuid(),
  state: tradeStateSchema,
  revision: z.number().int().min(0),
  /** Which participant the CALLER is. Never an id the caller supplied. */
  viewerAccountId: z.string().uuid(),
  initiator: tradeParticipantWireSchema,
  counterparty: tradeParticipantWireSchema,
  fee: tradeFeeWireSchema,
  /** Server-computed deadline for the current phase, ISO. Null when terminal. */
  expiresAt: z.string().nullable(),
  /** The caller's own wallets, so the panel never has to guess availability. */
  wallets: z.array(tradeWalletWireSchema),
  /** True once both accepted the same revision. */
  locked: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type TradeWire = z.infer<typeof tradeWireSchema>;

export const tradeResponseSchema = z.object({ trade: tradeWireSchema });
export type TradeResponse = z.infer<typeof tradeResponseSchema>;

/** One row of the inbox. Deliberately smaller than the full trade. */
export const tradeSummaryWireSchema = z.object({
  tradeId: z.string().uuid(),
  state: tradeStateSchema,
  revision: z.number().int().min(0),
  /** The OTHER participant, from the caller's point of view. */
  counterpartyName: z.string(),
  counterpartyId: z.string().uuid(),
  /** True when the caller was invited rather than doing the inviting. */
  incoming: z.boolean(),
  feeComplete: z.boolean(),
  expiresAt: z.string().nullable(),
  updatedAt: z.string(),
});

export const tradeInboxResponseSchema = z.object({
  contractVersion: z.literal(TRADE_CONTRACT_VERSION),
  trades: z.array(tradeSummaryWireSchema),
  nextCursor: z.string().nullable(),
});
export type TradeInboxResponse = z.infer<typeof tradeInboxResponseSchema>;

/**
 * A searchable player. Three fields, and no fourth.
 *
 * No e-mail, no level, no balance, no last-seen, no VIP expiry: a search by
 * name must not become a way to enumerate who is worth robbing. `vipActive` is
 * here only because a direct trade requires it on both sides, so inviting
 * somebody who cannot trade should fail before the invitation, not after.
 */
export const tradePlayerWireSchema = z.object({
  accountId: z.string().uuid(),
  displayName: z.string(),
  vipActive: z.boolean(),
});

export const tradePlayerSearchResponseSchema = z.object({
  players: z.array(tradePlayerWireSchema).max(PLAYER_SEARCH_MAX_RESULTS),
});
export type TradePlayerSearchResponse = z.infer<typeof tradePlayerSearchResponseSchema>;

/* -------------------------------------------------------------------------- */
/*  Permissions, as the client sees them                                      */
/* -------------------------------------------------------------------------- */

/**
 * What this account may do, resolved by the SERVER.
 *
 * Shipped so the UI can explain a refusal before it happens rather than after,
 * and never so the UI can decide one: every route re-resolves the same answer
 * from the same resolvers, and a client that lied to itself simply gets a 403.
 */
export const tradePermissionsWireSchema = z.object({
  /** Both participants need VIP. This is the caller's half of that. */
  directTrade: z.boolean(),
  /** VIP is NOT required. Everybody enabled may use the item marketplace. */
  itemMarketplace: z.boolean(),
  /** Which fee schedule a sale of this account's would be charged under. */
  marketplaceFeePercent: z.union([
    z.literal(MARKET_FEE_VIP_PERCENT),
    z.literal(MARKET_FEE_NON_VIP_PERCENT),
  ]),
  marketplaceFeeTier: z.enum(['vip', 'standard']),
});
export type TradePermissionsWire = z.infer<typeof tradePermissionsWireSchema>;

/** The fee constant, restated on the wire so a client never hardcodes it. */
export const DIRECT_TRADE_FEE_POINTS_WIRE = DIRECT_TRADE_FEE_POINTS.toString(10);
